import { omit, orderBy, partition } from 'lodash';
import type { ClearEvidenceResult, RestoredMappingReference } from 'services/captable';
import type { MappingReferenceData } from 'services/reference-generator/types';
import { referenceFieldNameSet } from './aiFieldMap';

type AnySecurity = Record<string, any>;

/** Drops the rows whose id is in `ids`; an untouched list keeps its identity. */
export const withoutIds = (refs: any[] | undefined, ids: Set<number>): any[] | undefined =>
  Array.isArray(refs) && refs.some(ref => ids.has(ref?.id)) ? refs.filter(ref => !ids.has(ref?.id)) : refs;

// A mapping row's id; the generated security type also spells rows as strings or null.
const rowId = (ref: unknown): number | null | undefined =>
  ref != null && typeof ref === 'object' ? (ref as { id?: number | null }).id : undefined;

/** Drop retired rows and merge in restored rows, whose server copy replaces a held twin; an untouched list keeps its identity. */
export const replaceMappingRows = <T>(refs: T[] | undefined, retiredIds: number[], restored: T[]): T[] | undefined => {
  const kept: T[] | undefined = withoutIds(refs, new Set(retiredIds));
  if (restored.length === 0) {
    return kept;
  }
  const restoredIds = new Set<unknown>(restored.map(rowId).filter(id => id != null));
  // Unsaved picks stay in front; saved rows follow by id, newest first, as the server orders them.
  const [unsaved, saved] = partition(
    [...(kept ?? []).filter(ref => !restoredIds.has(rowId(ref))), ...restored],
    ref => rowId(ref) == null
  );
  return [...unsaved, ...orderBy(saved, ref => rowId(ref), 'desc')];
};

/** One security's restored rows, in the grid shape. */
export const restoredRowsOnSecurity = (
  restored: RestoredMappingReference[],
  securityId: number | undefined
): MappingReferenceData[] =>
  // The store keeps a row a manual edit took off its cell: the row stays live until Save retires it.
  restored.filter(row => row.security_id === securityId).map(row => omit(row, 'security_id'));

/** One grid cell's restored rows: its security, a spelling of its field, and none a manual edit took off it. */
export const restoredRowsOnCell = (
  restored: RestoredMappingReference[],
  cell: { columnId?: number | string | null; alias?: string; retiredMappingIds?: number[] }
): MappingReferenceData[] => {
  const fieldNames = referenceFieldNameSet(cell.alias ? [cell.alias] : []);
  const editedOff = new Set<number | undefined>(cell.retiredMappingIds);
  return restoredRowsOnSecurity(restored, Number(cell.columnId)).filter(
    row => fieldNames.has(row.field_name) && !editedOff.has(row.id)
  );
};

// Drops the AI suggestion entries stored under any of these field names; untouched findings keep their identity.
const withoutSuggestions = (aiFinding: any, fieldNames: Set<string> | undefined): any => {
  const metadata = aiFinding?.field_metadata;
  if (!fieldNames || !metadata || !Object.keys(metadata).some(key => fieldNames.has(key))) {
    return aiFinding;
  }
  return {
    ...aiFinding,
    field_metadata: Object.fromEntries(Object.entries(metadata).filter(([key]) => !fieldNames.has(key))),
  };
};

/** Mirrors a server clear onto the cached securities: retired rows go and restored Excel references come back. */
export const applyClearToSecurities = (
  securities: AnySecurity[],
  result: Partial<ClearEvidenceResult>
): AnySecurity[] => {
  const citationIds = new Set(result.retired_citation_ids ?? []);
  const clearedFields = new Map<number, Set<string>>();
  (result.cleared_cells ?? []).forEach(({ security_id: securityId, field_name: fieldName }) => {
    clearedFields.set(
      securityId,
      new Set([...(clearedFields.get(securityId) ?? []), ...referenceFieldNameSet([fieldName])])
    );
  });

  const next = securities.map(security => {
    const citations = withoutIds(security.citation_references, citationIds);
    const mappings = replaceMappingRows(
      security.mapping_references,
      result.retired_mapping_ids ?? [],
      restoredRowsOnSecurity(result.restored_mappings ?? [], security.id)
    );
    const aiFinding = withoutSuggestions(security.ai_finding, clearedFields.get(security.id));
    const unchanged =
      citations === security.citation_references &&
      mappings === security.mapping_references &&
      aiFinding === security.ai_finding;
    return unchanged
      ? security
      : { ...security, citation_references: citations, mapping_references: mappings, ai_finding: aiFinding };
  });
  return next.every((security, index) => security === securities[index]) ? securities : next;
};
